import EntityViewDisplayFieldFormatter from "../../lib/entity_view_display_field_formatters";
import JsonApiFilterManager from "../../lib/JsonApiFilterManager";
import raftGenericHelper from "../../lib/raft_generic_helper";
import { jsonClient, restClient } from "../../lib/raftApi";
import store from "../index";
import { usePermissions } from "../../composables/usePermissions";

const methods = {
  overrideLabel(fieldInfo, data) {
    if (typeof data.display_settings === "undefined") {
      return;
    }
    if (
      typeof data.display_settings.third_party_settings
        .bioraft_core_components === "undefined"
    ) {
      return;
    }
    if (
      typeof data.display_settings.third_party_settings.bioraft_core_components
        .label_override === "undefined"
    ) {
      return;
    }
    fieldInfo.label =
      data.display_settings.third_party_settings.bioraft_core_components.label_override;
  },
  sortingChangedEvent(sortEvent) {
    // @todo roche_work Handle sorting.
    switch (sortEvent.sortBy) {
      case "og_audience[0].title":
        sortEvent.sortBy = "og_audience.title";
        break;

      default:
        break;
    }
    const sortPrefix = sortEvent.sortDesc ? "-" : "";
    // @todo TODO roche_work Handle sorting.
    // this.updateBioMaterialsAsync();
    return sortPrefix;
  },
};

// Needed to allow module reuse, for example when displaying multiple bio
// materials on the same page.
// @see https://vuex.vuejs.org/guide/modules.html#module-reuse
export default function BiologicalsListingStore() {
  return {
    namespaced: true,
    state() {
      return {
        baseQueries: {
          include: "og_audience",
          "fields[node--laboratory]": "title",
          "fields[node--department]": "title",
          "fields[node--raft_unit]": "title",
          sort: "name",
        },
        materialType: "",
        materialTypeLabel: "",
        isBusy: false,
        displayData: {},
        bioMaterials: [],
        bioMaterialBundles: [],
        bioMaterialAllowedValues: [],
        bioMaterialTypeIds: [],
        bioMaterialsSupportingPlasmidVectors: [],
        fields: [],
        filters: [],
        filterData: {},
        response: {},
        currentPage: 1,
        totalMaterials: 0,
        updatingMaterialsStatus: "",
        filterValues: {},
        error: "",
        refs: {},
        groupNid: 0,
        inLibrary: 0,
        materialsPerPage: 25,
        displayId: "",
      };
    },
    getters: {
      getBaseQueries: (state) => state.baseQueries,
      getDisplayId: (state) => state.displayId,
      getDisplayData: (state) => state.displayData,
      getMaterialType: (state) => state.materialType,
      getResponse: (state) => state.response,
      getMaterialTypeLabel: (state) => state.materialTypeLabel,
      getFilterData: (state) => state.filterData,
      getFilters: (state) => state.filters,
      getFields: (state) => {
        const { hasPermissions } = usePermissions(state.groupNid);
        const ret = state.fields;
        if (
          hasPermissions([
            "edit bio_material entities",
            "add bio_material entities",
          ])
        ) {
          ret.push({ key: "edit_url", label: "Edit", sortable: false });
          if (state.groupNid) {
            ret.push({ key: "remove_modal", label: "", sortable: false });
          } else {
            ret.push({ key: "remove_link", label: "", sortable: false });
          }
        }
        return ret;
      },
      getError: (state) => state.error,
      getRefs: (state) => state.refs,
      getBusy: (state) => state.isBusy,
      getCurrentPage: (state) => state.currentPage,
      getTotalMaterials: (state) => state.totalMaterials,
      getUpdatingMaterialsStatus: (state) => state.updatingMaterialsStatus,
      getBioMaterials: (state) => state.bioMaterials,
      getBioMaterialBundles: (state) => state.bioMaterialBundles,
      getBioMaterialTypeIds: (state) => state.bioMaterialTypeIds,
      getBioMaterialAllowedValues: (state) => state.bioMaterialAllowedValues,
      getBioMaterialsSupportingPlasmidVectors: (state) =>
        state.bioMaterialsSupportingPlasmidVectors,
      getGroupNid: (state) => state.groupNid,
      getInLibrary: (state) => state.inLibrary,
      getMaterialsPerPage: (state) => state.materialsPerPage,
      getMaterialUrlBase(_, getters) {
        let url = "/";
        if (getters.getGroupNid) {
          url += "node/" + getters.getGroupNid + "/biologicals/bio/";
        } else {
          url += "raft/biologicals/";
        }
        url += getters.getMaterialType + "/";
        return url;
      },
      getJsonApiPath(_, getters) {
        return "bio_material/" + getters.getMaterialType;
      },
      getSkippedFields(_, getters) {
        // The name field is what we search and maybe sort by, but never display.
        const skippedFields = ["name"];
        if (getters.getInLibrary || getters.getGroupNid) {
          skippedFields.push("og_audience");
        }
        return skippedFields;
      },
      getSkippedFilters(_, getters) {
        const skippedFields = [];
        if (getters.getInLibrary || getters.getGroupNid) {
          skippedFields.push("og_audience");
        }
        if (
          (getters.getMaterialType === "cell_line" ||
            getters.getMaterialType === "tissue") &&
          store.state.config.bioRegulations !== "swiss"
        ) {
          skippedFields.push("field_risk_group");
        }
        return skippedFields;
      },
      async getMaterialsGroupsList(state) {
        if (state.inLibrary) {
          return [];
        }
        const options = {
          path: "/bio-materials-groups-list/" + state.materialType,
          queries: JsonApiFilterManager.queries,
        };
        return restClient.get(options).catch((e) => {
          // eslint-disable-next-line no-console
          console.error(e);
        });
      },
    },
    mutations: {
      setMaterialType: (state, type) => (state.materialType = type),
      setDisplayId: (state, data) => (state.displayId = data),
      setDisplayData: (state, data) => (state.displayData = data),
      setMaterialTypeLabel: (state, label) => (state.materialTypeLabel = label),
      setFilterData: (state, filterData) => (state.filterData = filterData),
      setFields: (state, fields) => (state.fields = fields),
      setFilters: (state, filters) => (state.filters = filters),
      setError: (state, error) => (state.error = error),
      setRefs: (state, refs) => (state.refs = refs),
      setBusy: (state, busy) => (state.isBusy = busy),
      setCurrentPage: (state, page) => (state.currentPage = page),
      setTotalMaterials: (state, total) => (state.totalMaterials = total),
      setUpdatingMaterialsStatus: (state, status) =>
        (state.updatingMaterialsStatus = status),
      setResponse: (state, response) => (state.response = response),
      setBioMaterials: (state, bioMaterials) =>
        (state.bioMaterials = bioMaterials),
      setBioMaterialBundles: (state, bundles) =>
        (state.bioMaterialBundles = bundles),
      setBioMaterialAllowedValues: (state, values) =>
        (state.bioMaterialAllowedValues = values),
      setBioMaterialTypeIds: (state, values) =>
        (state.bioMaterialTypeIds = values),
      setBioMaterialsSupportingPlasmidVectors: (state, values) =>
        (state.bioMaterialsSupportingPlasmidVectors = values),
      setGroupNid: (state, groupNid) => (state.groupNid = groupNid),
      setInLibrary: (state, inLibrary) => (state.inLibrary = inLibrary),
    },
    actions: {
      initializeListings({ commit, dispatch }, data) {
        commit("setMaterialType", data.materialType);
        commit("setDisplayId", data.displayId);
        commit("setRefs", data.refs);
        commit("setInLibrary", data.inLibrary);
        commit("setGroupNid", data.groupNid);
        dispatch("setDefaultFilters");
        dispatch("fetchBioMaterialBundles").then(() => {
          dispatch("getDisplayInfo");
        });
      },
      setDefaultFilters({ getters }) {
        JsonApiFilterManager.queries = getters.getBaseQueries;
        JsonApiFilterManager.queries["page[limit]"] =
          getters.getMaterialsPerPage;
        JsonApiFilterManager.addFilter(
          "in_library",
          "in_library",
          getters.getInLibrary,
        )
          .addFilter("status", "status", 1)
          .addFilter("operative_status", "operative_status", 1)
          .addFilter("workflow_status", "workflow_status", 1);

        if (getters.getGroupNid > 0) {
          JsonApiFilterManager.addFilter(
            "group_id",
            "og_audience.nid",
            getters.getGroupNid,
          );
          JsonApiFilterManager.queries["rog-id"] = getters.getGroupNid;
          JsonApiFilterManager.queries["rog-type"] = "node";
        } else if (!getters.getInLibrary) {
          // Not on a group page but not looking for library materials. Filter
          // out materials for inactive and unpublished groups.
          JsonApiFilterManager.addFilter(
            "group-active",
            "og_audience.operative_status",
            1,
          );
          JsonApiFilterManager.addFilter(
            "group-published",
            "og_audience.status",
            1,
          );
        }
      },
      async fetchBioMaterialBundles({ commit, dispatch, getters }) {
        if (getters.getBioMaterialsSupportingPlasmidVectors.length > 0) {
          return;
        }
        // Don't cancel overlapping requests, since we may have several
        // independent material stores on one page.
        const bundleResponseData = await restClient
          .get({ path: "biological/biological-material/bundles", queries: {} })
          .catch((error) => dispatch("updateError", error));
        if (bundleResponseData) {
          commit("setBioMaterialBundles", bundleResponseData);
          const bundleIds = [];
          const allowedValues = {};
          const plasmidVectorTypes = [];
          bundleResponseData.forEach((bundle) => {
            bundleIds.push(bundle.id);
            allowedValues[bundle.id] = bundle.label;
            if (bundle.has_plasmid_vector_field) {
              plasmidVectorTypes.push(bundle.id);
            }
          });
          commit("setBioMaterialTypeIds", bundleIds);
          commit("setBioMaterialAllowedValues", allowedValues);
          commit("setBioMaterialsSupportingPlasmidVectors", plasmidVectorTypes);
        }
      },
      async getDisplayInfo({ commit, dispatch, getters }) {
        let displayType = "institutional_listing_table_filters";
        if (getters.getGroupNid) {
          displayType = "listing_table_filters";
        }
        const path =
          "entity-view-display/bio_material/" + getters.getMaterialType;
        const display = getters.getDisplayId;
        const queries = {
          display_id: [display, displayType],
          wrap_allowed_values: 1,
        };
        const restResponseData = await restClient
          .get({ path, queries })
          .catch((error) => dispatch("updateError", error));
        const data = restResponseData?.[getters.getMaterialType];
        commit("setDisplayData", data?.[display]);
        commit("setMaterialTypeLabel", data?.["bundle-label"]);
        commit("setFilterData", data?.[displayType]);
        dispatch("buildFields");
        dispatch("buildFilters");
        dispatch("updateBioMaterialsAsync");
      },
      buildFields({ commit, getters }) {
        const fields = [];
        const displayData = getters.getDisplayData;
        if (displayData) {
          Object.keys(displayData).forEach((fieldName) => {
            if (getters.getSkippedFields.includes(fieldName)) {
              return;
            }
            const fieldData = {
              key: fieldName,
              label: displayData[fieldName].label,
              formatter: (value) =>
                EntityViewDisplayFieldFormatter.fieldFormatter(
                  value,
                  displayData[fieldName],
                ),
              sortable: false,
            };
            methods.overrideLabel(fieldData, displayData[fieldName]);
            fields.push(fieldData);
          });
        }
        commit("setFields", fields);
      },
      buildFilters({ commit, getters }) {
        const filterData = getters.getFilterData;
        const filters = [];
        if (filterData) {
          Object.keys(filterData).forEach((fieldName) => {
            if (getters.getSkippedFilters.includes(fieldName)) {
              return;
            }
            const filterInfo = {
              key: fieldName,
              label: filterData[fieldName].label,
              type: filterData[fieldName].definition.type,
              definition: filterData[fieldName].definition,
              displaySettings: filterData[fieldName].display_settings,
            };
            methods.overrideLabel(filterInfo, filterData[fieldName]);
            filters.push(filterInfo);
          });
        }
        commit("setFilters", filters);
      },
      async updateBioMaterials({ commit, dispatch, getters }) {
        commit("setUpdatingMaterialsStatus", "in-progress");
        commit("setBusy", true);
        if (getters.getCurrentPage > 1) {
          JsonApiFilterManager.setPageOffset(
            (getters.getCurrentPage - 1) * getters.getMaterialsPerPage,
          );
        } else {
          JsonApiFilterManager.setPageOffset(0);
        }
        const queries = raftGenericHelper.cloneObject(
          JsonApiFilterManager.queries,
        );
        if (
          getters.getBioMaterialsSupportingPlasmidVectors.includes(
            getters.getMaterialType,
          )
        ) {
          queries.include += ",field_biomat_plasmid_vector_type";
        }
        const materialsResponseData = await jsonClient.get({
          path: getters.getJsonApiPath,
          queries,
        });
        commit("setResponse", materialsResponseData);
        commit(
          "setTotalMaterials",
          parseInt(materialsResponseData.meta.count, 10),
        );
        commit("setBusy", false);
        dispatch("watchResponse", materialsResponseData);
      },
      async updateBioMaterialsAsync({ commit, dispatch }) {
        // @todo roche_work Verify axios is making parallel requests.
        try {
          await dispatch("updateBioMaterials");
        } catch (e) {
          commit("setUpdatingMaterialsStatus", "error");
          commit("setBusy", false);
        }
      },
      setBusy({ commit, dispatch }, busy) {
        commit("setBusy", busy);
        dispatch("tableResetScroll");
        dispatch("refreshTable");
      },
      tableResetScroll({ getters }) {
        getters.getRefs.table.$el.scrollTo(0, 0);
      },
      refreshTable({ getters }) {
        getters.getRefs.table.refresh();
      },
      updateCurrentPage({ commit, dispatch }, newPage) {
        commit("setCurrentPage", newPage);
        dispatch("updateBioMaterialsAsync");
      },
      updateFilters({ commit, dispatch }, filterValues) {
        Object.keys(filterValues).forEach((fieldName) => {
          const filterOpts = filterValues[fieldName];
          if (Array.isArray(filterOpts.value)) {
            // @todo r4264 This doesn't work as expected for multivalue selects.
            if (filterOpts.value.includes(0) || filterOpts.value.length === 0) {
              JsonApiFilterManager.deleteFiltersByName([filterOpts.name]);
            } else {
              JsonApiFilterManager.addMultivalueNamedFilter(
                filterOpts.name,
                fieldName,
                filterOpts.value,
              );
            }
          } else if (filterOpts.value === null || filterOpts.value === "") {
            if (filterOpts.operator === "IS NULL") {
              JsonApiFilterManager.addNullFilter(filterOpts.name, fieldName);
            } else if (filterOpts.operator === "IS NOT NULL") {
              JsonApiFilterManager.addNotNullFilter(filterOpts.name, fieldName);
            } else {
              JsonApiFilterManager.deleteFiltersByName([filterOpts.name]);
            }
          } else {
            JsonApiFilterManager.addFilter(
              filterOpts.name,
              fieldName,
              filterOpts.value,
              filterOpts.operator,
            );
          }
        });
        commit("setCurrentPage", 1);
        dispatch("updateBioMaterialsAsync");
      },
      watchResponse({ commit, dispatch, getters }, newMaterials) {
        const materials = newMaterials.deserialized;
        const bioMaterials = [];
        const fieldKey = getters.getFields?.[0]?.key;
        for (let i = 0, len = materials.length; i < len; i++) {
          const uuid = materials[i].id;
          let has_material_audience_link = false;
          let material_audience_link = "";
          let material_audience_title = "";
          if (0 in materials[i].og_audience) {
            if (
              materials[i].og_audience[0].hasOwnProperty("resourceIdObjMeta")
            ) {
              material_audience_link =
                "/node/" +
                materials[i].og_audience[0].resourceIdObjMeta
                  .drupal_internal__target_id;
              material_audience_title = materials[i].og_audience[0].title;
              has_material_audience_link = true;
            }
          }
          if (fieldKey && !materials[i][fieldKey].custom_slot) {
            materials[i][fieldKey] = {
              url: getters.getMaterialUrlBase + materials[i].id,
              text: materials[i][fieldKey],
              custom_slot: "internal-link",
            };
          }
          if (has_material_audience_link) {
            materials[i].og_audience = {
              url: material_audience_link,
              text: material_audience_title,
              custom_slot: "og-audience",
            };
          }
          materials[i].edit_url = {
            url: getters.getMaterialUrlBase + uuid + "/edit",
            text: "Edit",
            custom_slot: "edit-link",
          };
          materials[i].remove_modal = {
            id: uuid,
            custom_slot: "remove-modal",
          };
          materials[i].remove_link = {
            url: getters.getMaterialUrlBase + uuid + "/remove",
            text: "Remove",
            custom_slot: "remove-link",
          };
          bioMaterials.push(materials[i]);
        }
        commit("setBioMaterials", bioMaterials);
        if (getters.getBioMaterials.length < 1) {
          dispatch("tableResetScroll");
        }
        commit("setUpdatingMaterialsStatus", "success");
      },
      updateError({ commit }, error) {
        let message = error.message;
        if (error.data) {
          // This is for the JSON:API case.
          if (error.data.hasOwnProperty("errors")) {
            Object.values(error.data.errors).forEach((item) => {
              if (item.hasOwnProperty("detail")) {
                message += ". " + item.detail;
              }
            });
          }
          // This is for Drupal core responses.
          if (typeof error.data === "string") {
            // Strip HTML tags that appear in exception messages.
            const div = document.createElement("div");
            div.innerHTML = error.data;
            message += ". " + div.innerText;
          } else if (error.data.hasOwnProperty("message")) {
            message += ". " + error.data.message;
          }
        }
        commit("setError", message);
      },
      async removeMaterial({ commit, dispatch, getters }, id) {
        commit("setBusy", true);
        const editData = {
          id: id,
          type: "bio_material--" + this.materialType,
          attributes: {
            operative_status: 0,
          },
        };
        const path = getters.getJsonApiPath + "/" + id;
        const queries = {};
        if (this.getters.config.groupContext) {
          queries["rog-type"] = "node";
          queries["rog-id"] = this.getters.config.groupContext;
        }
        await jsonClient.patch({ path, queries, data: editData }).catch((e) => {
          // eslint-disable-next-line no-console
          console.error(e);
        });
        // @todo RAFT-4264 Handle error.
        dispatch("updateBioMaterialsAsync");
      },
    },
  };
}
